import { useState } from "react";
import { cn } from "@/lib/cn";

/**
 * ChevronFlow — a horizontal, nested-chevron workflow diagram.
 *
 * Each step is shaped like a right-pointing chevron: pointed right edge,
 * matching notch on the left edge (except first / last, which have flat
 * outer edges). Hover reveals the step's optional description and inverts
 * the tile for emphasis.
 *
 * Falls back to a compact vertical list on small screens where nested
 * chevrons don't fit gracefully.
 *
 * Usage:
 *   <ChevronFlow
 *     steps={[
 *       { label: "Complete Survey", description: "…" },
 *       { label: "Compatibility Analysis" },
 *       …
 *     ]}
 *   />
 */

const CHEVRON_DEPTH = 18; // px of the leading / trailing point

const clipFor = (i, total) => {
  const d = `${CHEVRON_DEPTH}px`;
  if (total === 1) return "none";
  if (i === 0) {
    return `polygon(0 0, calc(100% - ${d}) 0, 100% 50%, calc(100% - ${d}) 100%, 0 100%)`;
  }
  if (i === total - 1) {
    return `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${d} 50%)`;
  }
  return `polygon(0 0, calc(100% - ${d}) 0, 100% 50%, calc(100% - ${d}) 100%, 0 100%, ${d} 50%)`;
};

const paddingFor = (i, total) => {
  const first = i === 0;
  const last = i === total - 1;
  return cn(
    first ? "pl-5" : "pl-9",
    last ? "pr-5" : "pr-9",
    "py-5",
  );
};

export const ChevronFlow = ({ steps = [], testId }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const total = steps.length;

  return (
    <div
      data-testid={testId || "chevron-flow"}
      className="w-full"
      onMouseLeave={() => setHoverIndex(null)}
    >
      {/* Desktop / tablet: horizontal nested chevrons */}
      <ol className="hidden gap-1 md:flex">
        {steps.map((s, i) => {
          const active = hoverIndex === i;
          return (
            <li
              key={s.id || i}
              data-testid={`chevron-step-${i + 1}`}
              onMouseEnter={() => setHoverIndex(i)}
              onFocus={() => setHoverIndex(i)}
              tabIndex={0}
              role="listitem"
              aria-label={`${i + 1}. ${s.label}`}
              className={cn(
                "group relative flex-1 cursor-default outline-none transition-colors duration-200 ease-out",
                paddingFor(i, total),
                active
                  ? "bg-foreground text-background"
                  : "bg-surface/70 text-foreground/80 hover:text-foreground",
              )}
              style={{ clipPath: clipFor(i, total) }}
            >
              <div
                className={cn(
                  "font-mono text-[10px] tabular-nums transition-colors",
                  active ? "text-background/70" : "text-muted-foreground",
                )}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-1 text-sm font-medium tracking-tight sm:text-[15px]">
                {s.label}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Hover detail — desktop only */}
      <div className="mt-4 hidden min-h-[3rem] md:block" aria-live="polite">
        {hoverIndex !== null && steps[hoverIndex]?.description ? (
          <p
            key={hoverIndex}
            data-testid="chevron-flow-detail"
            className="max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]"
          >
            <span className="font-medium text-foreground">
              {steps[hoverIndex].label}.
            </span>{" "}
            {steps[hoverIndex].description}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground/60">
            Hover a step to read its detail.
          </p>
        )}
      </div>

      {/* Mobile fallback: compact vertical list */}
      <ol className="flex flex-col gap-3 md:hidden">
        {steps.map((s, i) => (
          <li
            key={`m-${s.id || i}`}
            data-testid={`chevron-step-m-${i + 1}`}
            className="flex items-start gap-4 border-t border-border pt-3"
          >
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-medium tracking-tight text-foreground">
                {s.label}
              </div>
              {s.description && (
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {s.description}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default ChevronFlow;
